import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function validatePasswordStrength(pw) {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters.';
  if (!/\d/.test(pw)) return 'Must include at least one number.';
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(pw)) return 'Must include at least one special character (!@#$%^&* etc.).';
  return null;
}

export default function Login({ setUserId }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState('err');
  const [mode, setMode] = useState('login');
  const [resetVia, setResetVia] = useState(null);
  const navigate = useNavigate();

  const showMsg = (text, type = 'err') => { setMsg(text); setMsgType(type); };
  const clearMsg = () => setMsg(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    clearMsg();
    try {
      const res = await fetch('/api/users/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password })
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 400 && data.error && data.error.includes('before passwords')) {
          setMode('set-password');
          showMsg('This account needs a password. Please set one now.', 'ok');
          return;
        }
        throw new Error(data.error);
      }
      localStorage.setItem('string_user_id', data.id);
      setUserId(String(data.id));
      navigate('/account');
    } catch (err) {
      showMsg(err.message);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    clearMsg();
    const err = validatePasswordStrength(password);
    if (err) { showMsg(err); return; }
    try {
      const res = await fetch('/api/users/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      localStorage.setItem('string_user_id', data.id);
      setUserId(String(data.id));
      navigate('/account');
    } catch (err) {
      showMsg(err.message);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    clearMsg();
    try {
      const res = await fetch('/api/users/request-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResetVia(data.via);
      setMode('enter-code');
      showMsg(
        data.via === 'sms'
          ? 'A 6-digit code was sent to your phone. Enter it below.'
          : data.via === 'email'
          ? 'A 6-digit code was sent to your email. Enter it below.'
          : 'If an account exists for that phone or email, a code was sent.',
        'ok'
      );
    } catch (err) {
      showMsg(err.message);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    clearMsg();
    const err = validatePasswordStrength(newPassword);
    if (err) { showMsg(err); return; }
    try {
      const res = await fetch('/api/users/confirm-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, code, new_password: newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      localStorage.setItem('string_user_id', data.id);
      setUserId(String(data.id));
      navigate('/account');
    } catch (err) {
      showMsg(err.message);
    }
  };

  const pwHint = (
    <p style={{ fontSize: '0.8rem', color: '#aaa', margin: '0.25rem 0 0.75rem' }}>
      8+ characters, one number, one special character
    </p>
  );

  return (
    <div>
      {mode === 'login' && (
        <>
          <h2>Log In</h2>
          {msg && <div className={`msg ${msgType}`}>{msg}</div>}
          <form onSubmit={handleLogin}>
            <label>Phone or Email</label>
            <input
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="+17165551234 or you@example.com"
              required
            />
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            <button type="submit" style={{ marginTop: '0.5rem' }}>Log In</button>
          </form>
          <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#888' }}>
            <button type="button" onClick={() => { setMode('request-reset'); clearMsg(); }} style={{ background: 'none', border: 'none', color: '#5D4037', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '0.9rem' }}>
              Forgot password?
            </button>
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#888' }}>
            Don't have an account? <Link to="/messaging_opt_in" style={{ color: '#555' }}>Sign up</Link>
          </p>
        </>
      )}

      {mode === 'set-password' && (
        <>
          <h2>Set Your Password</h2>
          {msg && <div className={`msg ${msgType}`}>{msg}</div>}
          <form onSubmit={handleSetPassword}>
            <label>Phone or Email</label>
            <input value={identifier} onChange={e => setIdentifier(e.target.value)} placeholder="+17165551234 or you@example.com" required />
            <label>Create a Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="New password" required />
            {pwHint}
            <button type="submit" style={{ marginTop: '0.5rem' }}>Set Password & Log In</button>
          </form>
          <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
            <button type="button" onClick={() => { setMode('login'); clearMsg(); }} style={{ background: 'none', border: 'none', color: '#555', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}>Back to login</button>
          </p>
        </>
      )}

      {mode === 'request-reset' && (
        <>
          <h2>Reset Password</h2>
          <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
            Enter the phone number or email on your account and we'll send you a 6-digit code.
          </p>
          {msg && <div className={`msg ${msgType}`}>{msg}</div>}
          <form onSubmit={handleRequestReset}>
            <label>Phone or Email</label>
            <input
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              placeholder="+17165551234 or you@example.com"
              required
            />
            <button type="submit" style={{ marginTop: '0.5rem' }}>Send Reset Code</button>
          </form>
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#888' }}>
            <button type="button" onClick={() => { setMode('login'); clearMsg(); }} style={{ background: 'none', border: 'none', color: '#555', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}>Back to login</button>
          </p>
        </>
      )}

      {mode === 'enter-code' && (
        <>
          <h2>Enter Reset Code</h2>
          {msg && <div className={`msg ${msgType}`}>{msg}</div>}
          <form onSubmit={handleConfirmReset}>
            <label>6-Digit Code</label>
            <input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              required
              maxLength={6}
            />
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="New password"
              required
            />
            {pwHint}
            <button type="submit" style={{ marginTop: '0.5rem' }}>Set New Password & Log In</button>
          </form>
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#888' }}>
            Didn't receive a code?{' '}
            <button type="button" onClick={() => { setMode('request-reset'); setCode(''); setNewPassword(''); clearMsg(); }} style={{ background: 'none', border: 'none', color: '#5D4037', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}>
              Send again
            </button>
          </p>
        </>
      )}
    </div>
  );
}
